/**
 * Secret-chat session manager.
 *
 * Higher-level glue between:
 *   - the protocol primitives in `protocol.ts`,
 *   - the local IDB persistence in `storage.ts`,
 *   - the REST surface in `../../api/secretChats.ts`.
 *
 * Public surface used by UI:
 *
 *   ensureIdentityPublished()           — bootstraps identity + prekey bundle
 *                                          on first use, replenishes OPKs
 *   sendSecretMessage(chatId, peerId,
 *                     plaintext)         — encrypts + ships an envelope
 *   receiveSecretMessages(chatId, peerId,
 *                         envelopes)     — decrypts a batch of inbound rows
 *   getOrCreateSession(chatId, peerId,
 *                      direction)        — exposes the cached session for UI
 *                                          (e.g. to compute the safety number)
 *   destroyLocalSession(chatId)         — wipe local cache for one chat
 */

import {
  bytesToBase64Url,
  base64UrlToBytes,
  ecdsaSign,
  exportPublicSpki,
  generateEcdhKey,
  generateIdentitySigningKey,
  isWebCryptoAvailable,
  kdfRK,
  utf8ToBytes,
  type Bytes,
} from './crypto';
import {
  decodeEnvelope,
  decryptMessage,
  decryptMessageV2,
  encodeEnvelope,
  encodeEnvelopeV2,
  encryptMessage,
  encryptMessageV2,
  initiatorDeriveSession,
  responderDeriveSession,
  computeSafetyNumber,
  PROTOCOL_VERSION_V2,
  type SessionState,
  type SessionStateV2,
  type InitialEnvelopeMeta,
  type DecodedEnvelopeV2,
} from './protocol';
import {
  deleteSession,
  getIdentity,
  getLatestSignedPrekey,
  getMeta,
  getSession,
  putIdentity,
  putOneTimePrekey,
  putSession,
  putSignedPrekey,
  setMeta,
  takeOneTimePrekey,
  isSessionV2,
  type StoredSession,
  type StoredSessionV2,
  type AnyStoredSession,
} from './storage';
import { secretChatsApi, type PeerBundleResponse, type SecretMessageRow } from '@/api/secretChats';

const TARGET_OPK_POOL_SIZE = 20;

export class SecretChatUnsupportedError extends Error {
  constructor() {
    super('WebCrypto / IndexedDB unavailable — secret chats require a modern browser over a secure origin.');
  }
}

function assertSupported() {
  if (!isWebCryptoAvailable()) throw new SecretChatUnsupportedError();
  if (typeof indexedDB === 'undefined') throw new SecretChatUnsupportedError();
}

// ── Identity bootstrap ──────────────────────────────────────────────────────

async function generateAndStoreIdentity() {
  const signing = await generateIdentitySigningKey();
  // Identity DH key — also ECDSA-signed by `signing` so peers can authenticate it.
  const identityDh = await generateEcdhKey();
  await putIdentity({
    id: 'self',
    signing,
    identityDh,
    createdAt: Date.now(),
  });
  return { signing, identityDh };
}

async function generateAndStoreSignedPrekey(): Promise<{
  keyId: number;
  pair: CryptoKeyPair;
}> {
  const next = ((await getMeta<number>('nextSignedPrekeyId')) ?? 0) + 1;
  await setMeta('nextSignedPrekeyId', next);
  const pair = await generateEcdhKey();
  await putSignedPrekey({ keyId: next, pair, createdAt: Date.now() });
  return { keyId: next, pair };
}

async function generateAndStoreOpk(): Promise<{ keyId: number; publicKey: string }> {
  const next = ((await getMeta<number>('nextOpkId')) ?? 0) + 1;
  await setMeta('nextOpkId', next);
  const pair = await generateEcdhKey();
  await putOneTimePrekey({ keyId: next, pair });
  const spki = await exportPublicSpki(pair.publicKey);
  return { keyId: next, publicKey: bytesToBase64Url(spki) };
}

/**
 * Make sure this device has an identity, a signed prekey, and a healthy
 * pool of one-time prekeys, and that the corresponding bundle is published
 * on the server. Idempotent.
 */
export async function ensureIdentityPublished(): Promise<void> {
  assertSupported();

  let identity = await getIdentity();
  if (!identity) {
    const fresh = await generateAndStoreIdentity();
    identity = {
      id: 'self',
      signing: fresh.signing,
      identityDh: fresh.identityDh,
      createdAt: Date.now(),
    };
  }

  let spk = await getLatestSignedPrekey();
  if (!spk) {
    const fresh = await generateAndStoreSignedPrekey();
    spk = { keyId: fresh.keyId, pair: fresh.pair, createdAt: Date.now() };
  }

  // Build the published bundle. Sign IK_dh and SPK with the identity signing key.
  const ikSignSpki = await exportPublicSpki(identity.signing.publicKey);
  const ikDhSpki = await exportPublicSpki(identity.identityDh.publicKey);
  const spkSpki = await exportPublicSpki(spk.pair.publicKey);
  const ikDhSig = await ecdsaSign(identity.signing.privateKey, ikDhSpki);
  const spkSig = await ecdsaSign(identity.signing.privateKey, spkSpki);

  // Refresh the server-side bundle if any field changed (or this is a first
  // run). The endpoint is upsert-style so we can call it unconditionally.
  await secretChatsApi.publishBundle({
    identityKey: bytesToBase64Url(ikSignSpki),
    identityDhKey: bytesToBase64Url(ikDhSpki),
    identityDhSig: bytesToBase64Url(ikDhSig),
    signedPrekey: bytesToBase64Url(spkSpki),
    signedPrekeySig: bytesToBase64Url(spkSig),
    signedPrekeyId: spk.keyId,
  });

  // Top up OPKs.
  const remote = await secretChatsApi.countOneTimePrekeys();
  const missing = Math.max(0, TARGET_OPK_POOL_SIZE - remote.count);
  if (missing > 0) {
    const fresh: { keyId: number; publicKey: string }[] = [];
    for (let i = 0; i < missing; i += 1) {
      fresh.push(await generateAndStoreOpk());
    }
    await secretChatsApi.uploadOneTimePrekeys(fresh);
  }
}

// ── Session helpers ─────────────────────────────────────────────────────────

function storedToState(s: StoredSession): SessionState {
  return {
    direction: s.direction,
    ckSend: new Uint8Array(s.ckSend),
    ckRecv: new Uint8Array(s.ckRecv),
    ns: s.ns,
    nr: s.nr,
    mkSkipped: new Map(s.mkSkipped.map(([k, v]) => [k, new Uint8Array(v)])),
  };
}

function stateToStored(prev: StoredSession, state: SessionState): StoredSession {
  return {
    ...prev,
    direction: state.direction,
    ckSend: new Uint8Array(state.ckSend),
    ckRecv: new Uint8Array(state.ckRecv),
    ns: state.ns,
    nr: state.nr,
    mkSkipped: Array.from(state.mkSkipped.entries()).map(([k, v]) => [k, new Uint8Array(v)]),
    updatedAt: Date.now(),
  };
}

async function loadOwnIdentityOrThrow() {
  const id = await getIdentity();
  if (!id) throw new Error('No local secret-chat identity. Call ensureIdentityPublished() first.');
  return id;
}

/**
 * Initiator path (v1) — derive a fresh session against the peer's bundle and
 * persist it. Kept for backward compat; new sessions use initiatorBootstrapV2.
 */
// @ts-ignore — kept for v1 backward compat / tests
async function initiatorBootstrap(
  chatId: string,
  peerId: string,
): Promise<{ session: StoredSession; initMeta: InitialEnvelopeMeta }> {
  const me = await loadOwnIdentityOrThrow();
  const peerBundle: PeerBundleResponse = await secretChatsApi.fetchPeerBundle(peerId);
  const derived = await initiatorDeriveSession({
    ownIdentityDh: me.identityDh,
    peerBundle,
  });

  const ourIkSignSpki = await exportPublicSpki(me.signing.publicKey);
  const ourIkDhSpki = await exportPublicSpki(me.identityDh.publicKey);
  const safetyNumber = await computeSafetyNumber(
    ourIkSignSpki,
    base64UrlToBytes(peerBundle.identityKey),
  );

  const initMeta: InitialEnvelopeMeta = {
    ek: derived.initMeta.ek,
    spkId: derived.initMeta.spkId,
    opkId: derived.initMeta.opkId,
    ik: bytesToBase64Url(ourIkDhSpki),
    is: bytesToBase64Url(ourIkSignSpki),
  };

  const stored: StoredSession = {
    chatId,
    peerUserId: peerId,
    peerIkSignPubSpki: peerBundle.identityKey,
    ourIkSignPubSpki: bytesToBase64Url(ourIkSignSpki),
    direction: 'I',
    verified: false,
    safetyNumber,
    ckSend: new Uint8Array(derived.chainKey),
    ns: 0,
    ckRecv: new Uint8Array(derived.chainKey),
    nr: 0,
    mkSkipped: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await putSession(stored);
  return { session: stored, initMeta };
}

/**
 * Responder path — given the initiator's `init` envelope, derive the matching
 * session keys and persist them.
 */
async function responderBootstrap(
  chatId: string,
  peerId: string,
  initMeta: InitialEnvelopeMeta,
): Promise<StoredSession> {
  const me = await loadOwnIdentityOrThrow();
  const spk = await getLatestSignedPrekey();
  if (!spk) throw new Error('No local signed prekey — cannot respond to handshake');
  const opk = initMeta.opkId !== null ? await takeOneTimePrekey(initMeta.opkId) : null;

  const derived = await responderDeriveSession({
    ownIdentityDh: me.identityDh,
    ownSignedPrekey: spk.pair,
    ownOneTimePrekey: opk?.pair ?? null,
    initMeta,
  });

  const ourIkSignSpki = await exportPublicSpki(me.signing.publicKey);
  const safetyNumber = await computeSafetyNumber(
    ourIkSignSpki,
    base64UrlToBytes(initMeta.is),
  );

  const stored: StoredSession = {
    chatId,
    peerUserId: peerId,
    peerIkSignPubSpki: initMeta.is,
    ourIkSignPubSpki: bytesToBase64Url(ourIkSignSpki),
    direction: 'R',
    verified: false,
    safetyNumber,
    ckSend: new Uint8Array(derived.chainKey),
    ns: 0,
    ckRecv: new Uint8Array(derived.chainKey),
    nr: 0,
    mkSkipped: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await putSession(stored);
  return stored;
}

// ── v2 (Double Ratchet) session helpers ──────────────────────────────────────

function storedToStateV2(s: StoredSessionV2): SessionStateV2 {
  return {
    version: 2,
    direction: s.direction,
    rootKey: new Uint8Array(s.rootKey) as Bytes,
    DHs: s.DHs,
    DHr: s.DHr,
    CKs: s.CKs ? new Uint8Array(s.CKs) as Bytes : null,
    CKr: s.CKr ? new Uint8Array(s.CKr) as Bytes : null,
    ns: s.ns,
    nr: s.nr,
    pn: s.pn,
    mkSkipped: new Map(s.mkSkipped.map(([k, v]) => [k, new Uint8Array(v) as Bytes])),
  };
}

function stateToStoredV2(prev: StoredSessionV2, state: SessionStateV2): StoredSessionV2 {
  return {
    ...prev,
    rootKey: new Uint8Array(state.rootKey),
    DHs: state.DHs,
    DHr: state.DHr,
    CKs: state.CKs ? new Uint8Array(state.CKs) : null,
    CKr: state.CKr ? new Uint8Array(state.CKr) : null,
    ns: state.ns,
    nr: state.nr,
    pn: state.pn,
    mkSkipped: Array.from(state.mkSkipped.entries()).map(([k, v]) => [k, new Uint8Array(v)] as [string, Uint8Array]),
    updatedAt: Date.now(),
  };
}

/**
 * v2 Initiator bootstrap — X3DH then **initial DH ratchet step** to derive CKs.
 *
 * Phase 17.1 fix (2026-04-30): symmetric init с responder. Раньше:
 *   - Initiator: CKs = X3DH chainKey (без DH-step)
 *   - Responder: CKr = kdfRK(rootKey, DH(newDHs, peerEK)).chainKey
 * → разные ключи → AES-GCM auth fail на первом сообщении.
 *
 * Теперь оба делают тот же первый DH-step:
 *   DH(initiator_EK, responder_SPK) === DH(responder_SPK, initiator_EK)
 * → kdfRK(rootKey, DH).chainKey совпадает у обоих.
 *
 * Это Signal Double Ratchet pattern: Alice (initiator) делает DH-step при
 * init, Bob (responder) держит свой SPK keypair как DHs БЕЗ generate new.
 */
async function initiatorBootstrapV2(
  chatId: string,
  peerId: string,
): Promise<{ session: StoredSessionV2; initMeta: InitialEnvelopeMeta }> {
  const me = await loadOwnIdentityOrThrow();
  const peerBundle = await secretChatsApi.fetchPeerBundle(peerId);
  const derived = await initiatorDeriveSession({
    ownIdentityDh: me.identityDh,
    peerBundle,
  });

  const ourIkSignSpki = await exportPublicSpki(me.signing.publicKey);
  const ourIkDhSpki = await exportPublicSpki(me.identityDh.publicKey);
  const safetyNumber = await computeSafetyNumber(
    ourIkSignSpki,
    base64UrlToBytes(peerBundle.identityKey),
  );

  const initMeta: InitialEnvelopeMeta = {
    ek: derived.initMeta.ek,
    spkId: derived.initMeta.spkId,
    opkId: derived.initMeta.opkId,
    ik: bytesToBase64Url(ourIkDhSpki),
    is: bytesToBase64Url(ourIkSignSpki),
  };

  // v2: DHs = ephemeral key (EK), DHr = peer's SPK
  const { importEcdhPublicSpki, ecdhDeriveBits: ecdh } = await import('./crypto');
  const spkPub = await importEcdhPublicSpki(base64UrlToBytes(peerBundle.signedPrekey));

  // Phase 17.1 fix — initial DH ratchet step при bootstrap.
  // Initiator делает DH(EK_priv, peer_SPK_pub) и kdfRK с rootKey
  // → получает новый rootKey + CKs. Тот же DH у responder'а
  // (через DH(SPK_priv, EK_pub) — симметрично) → одинаковый chainKey.
  const dhOut = await ecdh(derived.ephemeral.privateKey, spkPub);
  const { newRK, chainKey: CKs } = await kdfRK(derived.rootKey as Bytes, dhOut);

  const stored: StoredSessionV2 = {
    version: 2,
    chatId,
    peerUserId: peerId,
    peerIkSignPubSpki: peerBundle.identityKey,
    ourIkSignPubSpki: bytesToBase64Url(ourIkSignSpki),
    direction: 'I',
    verified: false,
    safetyNumber,
    rootKey: new Uint8Array(newRK),    // обновлённый после DH-step
    DHs: derived.ephemeral,            // EK from X3DH
    DHr: spkPub,                       // Peer's SPK
    CKs: new Uint8Array(CKs),          // kdfRK(rootKey, DH(EK, SPK)).chainKey
    CKr: null,                         // Set on first recv after responder's DH-rotate
    ns: 0,
    nr: 0,
    pn: 0,
    mkSkipped: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await putSession(stored);
  return { session: stored, initMeta };
}

/**
 * v2 Responder bootstrap — X3DH then derive CKr from same initial DH-step
 * as initiator. NO new DH keypair generated at bootstrap — DHs = own SPK
 * keypair, ровно как в Signal Double Ratchet (Bob holds his SPK until first
 * outgoing message rotates it).
 *
 * Phase 17.1 fix: ранее генерировалась freshly newDHs и CKr выводился через
 * DH(newDHs, peerEK) — но initiator не делал DH-step и его CKs был
 * X3DH-chainKey, что не совпадало с CKr → fail.
 *
 * Теперь responder использует SPK как initial DHs, делает DH(SPK, peerEK)
 * — симметрично initiator'у → одинаковый CKr/CKs chainKey.
 */
async function responderBootstrapV2(
  chatId: string,
  peerId: string,
  initMeta: InitialEnvelopeMeta,
): Promise<StoredSessionV2> {
  const me = await loadOwnIdentityOrThrow();
  const spk = await getLatestSignedPrekey();
  if (!spk) throw new Error('No local signed prekey');
  const opk = initMeta.opkId !== null ? await takeOneTimePrekey(initMeta.opkId) : null;

  const derived = await responderDeriveSession({
    ownIdentityDh: me.identityDh,
    ownSignedPrekey: spk.pair,
    ownOneTimePrekey: opk?.pair ?? null,
    initMeta,
  });

  const ourIkSignSpki = await exportPublicSpki(me.signing.publicKey);
  const safetyNumber = await computeSafetyNumber(
    ourIkSignSpki,
    base64UrlToBytes(initMeta.is),
  );

  const { importEcdhPublicSpki, ecdhDeriveBits: ecdh } = await import('./crypto');
  const ekPub = await importEcdhPublicSpki(base64UrlToBytes(initMeta.ek));

  // Phase 17.1 fix — initial DH-step симметрично initiator'у:
  // DH(SPK_priv, EK_pub) === DH(EK_priv, SPK_pub) (ECDH commutativity).
  // → kdfRK(rootKey, DH).chainKey совпадает с CKs у initiator'а.
  // DHs остаётся равным SPK keypair (rotates on first outgoing message).
  const dhOut = await ecdh(spk.pair.privateKey, ekPub);
  const { newRK, chainKey: CKr } = await kdfRK(derived.rootKey as Bytes, dhOut);

  const stored: StoredSessionV2 = {
    version: 2,
    chatId,
    peerUserId: peerId,
    peerIkSignPubSpki: initMeta.is,
    ourIkSignPubSpki: bytesToBase64Url(ourIkSignSpki),
    direction: 'R',
    verified: false,
    safetyNumber,
    rootKey: new Uint8Array(newRK),
    DHs: spk.pair,                  // SPK keypair as initial DHs (Signal-style Bob)
    DHr: ekPub,                     // Initiator's EK
    CKs: null,                      // Set on first DH ratchet when responder отвечает
    CKr: new Uint8Array(CKr),       // kdfRK(rootKey, DH(SPK, EK)).chainKey
    ns: 0,
    nr: 0,
    pn: 0,
    mkSkipped: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await putSession(stored);
  return stored;
}

// ── Public surface ──────────────────────────────────────────────────────────

export interface DecryptedSecretMessage {
  id: string;
  chatId: string;
  senderId: string;
  isOutgoing: boolean;
  createdAt: string;
  text: string;
  expiresAt: string | null;
}

export async function getCachedSession(chatId: string): Promise<AnyStoredSession | null> {
  return getSession(chatId);
}

export async function destroyLocalSession(chatId: string): Promise<void> {
  await deleteSession(chatId);
}

export async function markSessionVerified(chatId: string, verified: boolean): Promise<void> {
  const s = await getSession(chatId);
  if (!s) return;
  await putSession({ ...s, verified });
}

/**
 * Encrypt and send a secret message. Bootstraps the initiator side of the
 * session if there isn't one yet. New sessions use v2 (Double Ratchet);
 * existing v1 sessions continue with v1.
 */
export async function sendSecretMessage(
  chatId: string,
  peerId: string,
  text: string,
): Promise<void> {
  assertSupported();
  let session: AnyStoredSession | null = await getSession(chatId);
  let isHandshake = false;
  let initMeta: InitialEnvelopeMeta | undefined;

  if (!session) {
    // New session → always use v2 (Double Ratchet).
    const bootstrap = await initiatorBootstrapV2(chatId, peerId);
    session = bootstrap.session;
    initMeta = bootstrap.initMeta;
    isHandshake = true;
  }

  if (isSessionV2(session)) {
    // v2 path
    const stateV2 = storedToStateV2(session);
    const { envelope: env, newState } = await encryptMessageV2(stateV2, chatId, utf8ToBytes(text));
    if (initMeta) env.init = initMeta;
    if (isHandshake) env.type = 'init';
    const dhPub = await exportPublicSpki(stateV2.DHs.publicKey);
    const encoded = encodeEnvelopeV2(
      env.type, dhPub, env.pn, env.n,
      base64UrlToBytes(env.iv), base64UrlToBytes(env.ct), initMeta,
    );
    await secretChatsApi.send(chatId, encoded, isHandshake);
    await putSession(stateToStoredV2(session, newState));
  } else {
    // v1 backward compat path
    const state = storedToState(session);
    const { n, iv, ciphertext, newState } = await encryptMessage(state, chatId, utf8ToBytes(text));
    const envelope = encodeEnvelope(isHandshake ? 'init' : 'msg', n, iv, ciphertext, initMeta);
    await secretChatsApi.send(chatId, envelope, isHandshake);
    await putSession(stateToStored(session, newState));
  }
}

/**
 * Decrypt a batch of envelopes fetched from the server. Updates the local
 * session as it goes. Skips any row that fails to decrypt (logged to console
 * with the chat id but without the ciphertext).
 */
export async function receiveSecretMessages(
  chatId: string,
  peerId: string,
  rows: SecretMessageRow[],
  myUserId: string,
): Promise<DecryptedSecretMessage[]> {
  assertSupported();
  const out: DecryptedSecretMessage[] = [];

  for (const row of rows) {
    if (row.senderId === myUserId) {
      // Echo of our own send. We don't need to "decrypt" it from storage,
      // but we still want it on the timeline. The server doesn't have the
      // plaintext — but the local session does, because we encrypted it
      // ourselves moments ago. We choose to surface a placeholder for outgoing
      // echoes; the local UI keeps the original text in component state when
      // we send. (See SecretChatPage for the local-echo merge.)
      out.push({
        id: row.id,
        chatId,
        senderId: row.senderId,
        isOutgoing: true,
        createdAt: row.createdAt,
        text: '',
        expiresAt: row.expiresAt,
      });
      continue;
    }

    let session = await getSession(chatId);
    let decoded;
    try {
      decoded = decodeEnvelope(row.envelope);
    } catch (err) {
      console.warn(`secret-chat: malformed envelope in ${chatId}`, (err as Error).message);
      continue;
    }

    const isV2Env = 'dh' in decoded && (decoded as DecodedEnvelopeV2).v === PROTOCOL_VERSION_V2;

    if (decoded.type === 'init') {
      if (session) {
        // We already have a session for this chat (e.g. an old init we
        // already consumed). Skip — the protocol initialization is once.
      } else {
        if (!decoded.init) {
          console.warn(`secret-chat: init envelope without handshake meta in ${chatId}`);
          continue;
        }
        // New sessions: v2 if envelope is v2, otherwise v1.
        session = isV2Env
          ? await responderBootstrapV2(chatId, peerId, decoded.init)
          : await responderBootstrap(chatId, peerId, decoded.init);
      }
    }

    if (!session) {
      console.warn(`secret-chat: msg envelope before handshake in ${chatId}`);
      continue;
    }

    try {
      let plaintext: Uint8Array;

      if (isSessionV2(session) && isV2Env) {
        // v2 Double Ratchet decrypt
        const stateV2 = storedToStateV2(session);
        const result = await decryptMessageV2(stateV2, chatId, decoded as DecodedEnvelopeV2);
        plaintext = result.plaintext;
        await putSession(stateToStoredV2(session, result.newState));
      } else if (!isSessionV2(session)) {
        // v1 symmetric ratchet decrypt
        const state = storedToState(session);
        const result = await decryptMessage(state, chatId, decoded.n, base64UrlToBytes(decoded.ct));
        plaintext = result.plaintext;
        await putSession(stateToStored(session as StoredSession, result.newState));
      } else {
        console.warn(`secret-chat: v1 envelope on v2 session in ${chatId}, skipping`);
        continue;
      }

      out.push({
        id: row.id,
        chatId,
        senderId: row.senderId,
        isOutgoing: false,
        createdAt: row.createdAt,
        text: new TextDecoder().decode(plaintext),
        expiresAt: row.expiresAt,
      });
    } catch (err) {
      console.warn(
        `secret-chat: decrypt failed in ${chatId} for message ${row.id}: ${(err as Error).message}`,
      );
      // Surface a tamper / replay marker so the UI shows that something is wrong.
      out.push({
        id: row.id,
        chatId,
        senderId: row.senderId,
        isOutgoing: false,
        createdAt: row.createdAt,
        text: '⚠️ [не удалось расшифровать сообщение]',
        expiresAt: row.expiresAt,
      });
    }
  }

  return out;
}
